import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FolderOpen, Globe2, Tag, Sparkles, Camera, Palette, FileText, LayoutTemplate,
  ClipboardCheck, ArrowLeft, Loader2, Plus, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

import { EditorToolSidebar } from '@/components/editor/EditorToolSidebar';
import { EditorBrandKitPanel } from '@/components/editor/EditorBrandKitPanel';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { OptimizeMediaPanel } from '@/components/editor/OptimizeMediaPanel';
import { OptimizePlatformsPanel } from '@/components/editor/OptimizePlatformsPanel';
import { OptimizeKeywordsPanel } from '@/components/editor/OptimizeKeywordsPanel';
import { OptimizeAIPanel } from '@/components/editor/OptimizeAIPanel';
import { OptimizeThumbnailPanel } from '@/components/editor/OptimizeThumbnailPanel';
import { OptimizeReviewPanel } from '@/components/editor/OptimizeReviewPanel';
import { PLATFORMS, platformLabel } from '@/lib/clipPlatformFormats';
import { scoreVariant, scoreLabel } from '@/lib/optimizationScoring';
import {
  getVariantsForContent, buildVariantRecord, upsertVariant, removeVariant, statusForReview,
  getBrandKeywords, saveBrandKeywords,
} from '@/lib/optimizeVariants';
import { getDefaultThumbnail } from '@/lib/thumbnailAssociations';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const API_URL = window.__BACKEND_URL;
const IMAGES_POSTS_KEY = (projectId) => `consistency_images_posts_${projectId}`;

const TOOL_CATEGORIES = [
  { key: 'media', label: 'Media', icon: FolderOpen },
  { key: 'platforms', label: 'Platforms', icon: Globe2 },
  { key: 'keywords', label: 'Keywords', icon: Tag },
  { key: 'ai', label: 'AI Tools', icon: Sparkles },
  { key: 'thumbnails', label: 'Thumbnails', icon: Camera },
  { key: 'brand', label: 'Brand Kit', icon: Palette },
  { key: 'metadata', label: 'Metadata', icon: FileText },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'review', label: 'Review', icon: ClipboardCheck },
];

/**
 * Optimize — a dedicated Production Tool workspace (separate from
 * Schedule, see Publish.jsx). Reuses the same editor shell pattern as
 * Main Editor / Clips / Images & Posts (icon rail + collapsible panel),
 * but the CENTER area is the primary, always-visible working surface
 * (title/description/CTA/hashtags/score/thumbnail/approve) per the
 * spec's "do not force users to visit each category" instruction — the
 * sidebar panels are optional deep-dive controls for Media, Platforms,
 * Keywords, AI, Thumbnails, Brand Kit, Metadata, Templates and a fuller
 * Review recap.
 */
export default function OptimizeWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const creatorIdParam = searchParams.get('creatorId');
  const assetIdParam = searchParams.get('assetId');

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [parentAssets, setParentAssets] = useState([]);
  const [brandkits, setBrandkits] = useState([]);
  const [imageDesigns, setImageDesigns] = useState([]);
  const [brandKeywordLibrary, setBrandKeywordLibrary] = useState([]);

  const creatorId = project?.creator_id || creatorIdParam;

  const [contentRef, setContentRef] = useState(null);
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [serverThumbnailUrl, setServerThumbnailUrl] = useState(null);

  const [groupVariants, setGroupVariants] = useState([]);
  const [activePlatform, setActivePlatform] = useState(null);

  const [toolCategory, setToolCategory] = useState('media');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setProject(data.project);
          setParentAssets(data.parent_assets || []);
        }
      } catch { toast.error('Could not load this project'); }
      setLoading(false);
    })();
    setImageDesigns((JSON.parse(localStorage.getItem(IMAGES_POSTS_KEY(projectId)) || '[]')));
  }, [projectId]);

  useEffect(() => {
    if (!creatorId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/brandkits/creator/${creatorId}`, { credentials: 'include' });
        const data = await res.json();
        let kits = data.brand_kits || data.brandkits || [];
        if (kits.length === 0) {
          const appRes = await fetch(`${API_URL}/api/brandkits/app`, { credentials: 'include' });
          const appData = await appRes.json();
          kits = appData.brand_kits || appData.brandkits || [];
        }
        setBrandkits(kits);
      } catch { /* brand kits optional */ }
    })();
    setBrandKeywordLibrary(getBrandKeywords(creatorId));
  }, [creatorId]);

  // Optional deep-link prefill from the Optimize tile / asset action.
  useEffect(() => {
    if (!assetIdParam || !parentAssets.length || contentRef) return;
    const asset = parentAssets.find((a) => a.id === assetIdParam);
    if (asset) selectVideo(asset);
  }, [assetIdParam, parentAssets]);

  useEffect(() => {
    if (!contentRef) { setGroupVariants([]); setActivePlatform(null); return; }
    const existing = getVariantsForContent(contentRef.id);
    setGroupVariants(existing);
    setActivePlatform(existing[0]?.platforms[0] || null);
  }, [contentRef?.id]);

  const selectVideo = (asset) => {
    setContentRef({ kind: 'video', id: asset.id, creatorId: asset.creator_id || creatorId, projectId, title: asset.title || asset.id, videoSourceId: asset.id });
    setClips([]); setServerThumbnailUrl(null);
    setClipsLoading(true);
    fetch(`${API_URL}/api/editor/projects/source/${asset.id}`, { credentials: 'include' })
      .then((r) => r.json()).then((data) => setClips(data.project?.clips || []))
      .catch(() => {}).finally(() => setClipsLoading(false));
    fetch(`${API_URL}/api/admin/content-sources/${asset.id}/thumbnail`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { thumbnail_url: null })).then((d) => setServerThumbnailUrl(d.thumbnail_url || null))
      .catch(() => {});
  };
  const selectClip = (clip) => {
    if (!clip) return;
    setContentRef({ kind: 'clip', id: clip.id, creatorId, projectId, title: clip.name, videoSourceId: contentRef?.videoSourceId });
  };
  const selectDesign = (design) => {
    setContentRef({ kind: 'design', id: design.id, creatorId, projectId, title: design.name });
    setClips([]); setServerThumbnailUrl(null);
  };

  const activeVariant = groupVariants.find((v) => v.platforms[0] === activePlatform) || null;

  const togglePlatform = (platformKey) => {
    if (!contentRef) return;
    const existing = groupVariants.find((v) => v.platforms[0] === platformKey);
    if (existing) {
      removeVariant(existing.id);
      const remaining = groupVariants.filter((v) => v.id !== existing.id);
      setGroupVariants(remaining);
      if (activePlatform === platformKey) setActivePlatform(remaining[0]?.platforms[0] || null);
      toast.info(`Removed the ${platformLabel(platformKey)} variant.`);
    } else {
      const record = buildVariantRecord({ contentRef, platform: platformKey, groupId: contentRef.id });
      upsertVariant(record);
      setGroupVariants([...groupVariants, record]);
      setActivePlatform(platformKey);
    }
  };

  const patchActiveVariant = (patch) => {
    if (!activeVariant) return;
    const next = { ...activeVariant, ...patch };
    upsertVariant(next);
    setGroupVariants(groupVariants.map((v) => (v.id === next.id ? next : v)));
  };

  const updateKeywordBucket = (bucket, arr) => patchActiveVariant({ keywords: { ...activeVariant.keywords, [bucket]: arr } });

  const applyAISuggestion = (field, value) => {
    if (field === 'contentKeywords') {
      updateKeywordBucket('content', Array.from(new Set([...activeVariant.keywords.content, ...value])));
      toast.success('Added suggested keywords to Current Content Keywords.');
      return;
    }
    patchActiveVariant({ [field]: value });
    toast.success(`Applied suggested ${field}.`);
  };

  const addBrandKeyword = (keyword) => {
    const next = Array.from(new Set([...brandKeywordLibrary, keyword]));
    setBrandKeywordLibrary(next);
    saveBrandKeywords(creatorId, next);
  };

  // ── Thumbnail resolution — exact priority order from the spec ──
  const resolvedThumbnail = useMemo(() => {
    if (!activeVariant) return { source: 'none', url: null };
    if (activeVariant.thumbnail?.source === 'override' && activeVariant.thumbnail.url) return activeVariant.thumbnail;
    if (contentRef?.kind !== 'design') {
      const sourceKind = contentRef.kind === 'clip' ? 'clip' : 'video';
      const linked = getDefaultThumbnail(projectId, sourceKind, contentRef.id, activePlatform);
      if (linked?.imageUrl) return { source: 'linked-default', url: linked.imageUrl };
    }
    if (contentRef?.kind === 'video' && serverThumbnailUrl) return { source: 'server-source', url: serverThumbnailUrl };
    return { source: 'none', url: null };
  }, [activeVariant, contentRef, activePlatform, serverThumbnailUrl, projectId]);

  const setThumbnailOverride = (dataUrl) => patchActiveVariant({ thumbnail: { source: 'override', url: dataUrl } });
  const clearThumbnailOverride = () => patchActiveVariant({ thumbnail: { source: 'none', url: null } });

  const selectedBrandKit = brandkits.find((b) => b.id === activeVariant?.brandkitId);
  const applyBrandKit = (brandkitId) => patchActiveVariant({ brandkitId });
  const applyBrandKitToAllPlatforms = (brandkitId) => {
    const updated = groupVariants.map((v) => { const n = { ...v, brandkitId }; upsertVariant(n); return n; });
    setGroupVariants(updated);
    toast.success('Applied this Brand Kit to every platform variant for this content.');
  };

  const liveScore = useMemo(() => {
    if (!activeVariant) return null;
    return scoreVariant({
      platform: activeVariant.platforms[0], title: activeVariant.title, description: activeVariant.description,
      cta: activeVariant.cta, hashtags: activeVariant.hashtags, keywords: activeVariant.keywords, hasThumbnail: !!resolvedThumbnail.url,
    });
  }, [activeVariant, resolvedThumbnail.url]);

  const setReviewStatus = (reviewStatus) => {
    if (!activeVariant) return;
    patchActiveVariant({
      reviewStatus, status: statusForReview(reviewStatus), score: liveScore,
      thumbnail: resolvedThumbnail, thumbnailLF: resolvedThumbnail.url, thumbnailSF: resolvedThumbnail.url,
      brandingApplied: !!activeVariant.brandkitId,
    });
    if (reviewStatus === 'approved') toast.success(`Approved — this ${platformLabel(activePlatform)} variant is ready in Scheduler.`);
    else if (reviewStatus === 'rejected') toast.info('Marked as rejected.');
  };

  const buildOptimizationTemplatePayload = () => (activeVariant ? {
    optimization: { title: activeVariant.title, description: activeVariant.description, cta: activeVariant.cta, hashtags: activeVariant.hashtags, keywords: activeVariant.keywords },
  } : {});
  const applyOptimizationTemplate = (tpl, editTpl) => {
    if (!editTpl.optimization || !activeVariant) { toast.error('This template has no reusable optimization data saved.'); return; }
    const o = editTpl.optimization;
    patchActiveVariant({
      title: o.title || activeVariant.title, description: o.description || activeVariant.description,
      cta: o.cta || activeVariant.cta, hashtags: o.hashtags || activeVariant.hashtags,
      keywords: {
        brand: Array.from(new Set([...activeVariant.keywords.brand, ...(o.keywords?.brand || [])])),
        platform: Array.from(new Set([...activeVariant.keywords.platform, ...(o.keywords?.platform || [])])),
        content: Array.from(new Set([...activeVariant.keywords.content, ...(o.keywords?.content || [])])),
      },
    });
    toast.success(`Applied "${editTpl.name}" — keywords were merged, not replaced.`);
  };

  const jumpTo = (category) => { setToolCategory(category); setSidebarCollapsed(false); };

  if (loading) {
    return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center" data-testid="optimize-loading"><Loader2 className="h-6 w-6 text-[#3A7BFF] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-white pb-10" data-testid="optimize-workspace">
      <div className="container mx-auto p-6 max-w-[1600px]">
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-[#8A93A6] mb-2">
              <Link to="/app/create" className="hover:text-white">Create</Link><span className="opacity-50">/</span>
              {projectId && <><Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span className="opacity-50">/</span>
                <Link to={`/app/create/workspace/project/${projectId}`} className="hover:text-white" data-testid="optimize-back-to-project-link">{project?.name || 'Project'}</Link><span className="opacity-50">/</span></>}
              <span className="text-[#C0C7D4]">Optimize</span>
            </div>
            <h1 className="text-2xl font-bold truncate" data-testid="optimize-title">Optimize</h1>
          </div>
          <div className="flex items-center gap-2">
            <ProductionToolsDropdown currentTool="optimize" creatorId={creatorId} projectId={projectId} assetId={contentRef?.id} />
            {projectId && (
              <Button variant="outline" size="sm" className="border-white/10" onClick={() => navigate(`/app/create/schedule?projectId=${projectId}&creatorId=${creatorId || ''}`)} data-testid="optimize-go-to-schedule-button">
                Go to Schedule
              </Button>
            )}
            <Button variant="outline" size="sm" className="border-white/10" onClick={() => navigate(projectId ? `/app/create/workspace/project/${projectId}` : '/app/create/workspace')} data-testid="optimize-back-button">
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Back to Project
            </Button>
          </div>
        </div>

        <div className="flex gap-3 items-stretch">
          <EditorToolSidebar categories={TOOL_CATEGORIES} activeCategory={toolCategory} onSelectCategory={setToolCategory} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed((c) => !c)} />

          {!sidebarCollapsed && (
            <div className="w-72 shrink-0 lg:h-[560px] overflow-y-auto" data-testid="optimize-tool-panel">
              <div className="glass rounded-xl p-4 lg:h-[560px] overflow-y-auto" data-testid={`optimize-panel-${toolCategory}`}>
                {toolCategory === 'media' && (
                  <OptimizeMediaPanel sources={parentAssets} selectedContentRef={contentRef} onSelectVideo={selectVideo} clips={clips} clipsLoading={clipsLoading} onSelectClip={selectClip} imageDesigns={imageDesigns} onSelectDesign={selectDesign} />
                )}
                {toolCategory === 'platforms' && <OptimizePlatformsPanel variants={groupVariants} onTogglePlatform={togglePlatform} disabled={!contentRef} />}
                {toolCategory === 'keywords' && (
                  <OptimizeKeywordsPanel variant={activeVariant} onUpdateBucket={updateKeywordBucket} brandKeywordLibrary={brandKeywordLibrary} onAddBrandKeyword={addBrandKeyword} disabled={!activeVariant} />
                )}
                {toolCategory === 'ai' && (
                  <OptimizeAIPanel variant={activeVariant} contentTitle={contentRef?.title} brandKit={selectedBrandKit} onApplySuggestion={applyAISuggestion} disabled={!activeVariant} />
                )}
                {toolCategory === 'thumbnails' && (
                  <OptimizeThumbnailPanel resolved={resolvedThumbnail} onSetOverride={setThumbnailOverride} onClearOverride={clearThumbnailOverride} disabled={!activeVariant} />
                )}
                {toolCategory === 'brand' && (
                  activeVariant ? (
                    <>
                      <EditorBrandKitPanel clip={{ brandkit_id: activeVariant.brandkitId, output_type: activePlatform }} brandkits={brandkits} defaultBrandKitId={brandkits[0]?.id} onApply={applyBrandKit} onApplyToAllOfType={applyBrandKitToAllPlatforms} nounLabel="platform variants" />
                      {selectedBrandKit?.tagline && (
                        <div className="mt-3 p-2.5 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="optimize-brand-tagline">
                          <p className="text-[10px] text-[#555E6E] mb-1">Brand tagline</p>
                          <p className="text-xs text-[#C0C7D4] mb-2">{selectedBrandKit.tagline}</p>
                          <Button size="sm" variant="outline" className="w-full border-white/10 text-xs" onClick={() => patchActiveVariant({ description: activeVariant.description ? `${activeVariant.description} ${selectedBrandKit.tagline}` : selectedBrandKit.tagline })} data-testid="optimize-insert-tagline-button">Insert into description</Button>
                        </div>
                      )}
                    </>
                  ) : <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="optimize-brand-empty">Select content and a platform first.</p>
                )}
                {toolCategory === 'metadata' && (
                  <div className="space-y-3" data-testid="optimize-metadata-panel">
                    <div className="p-3 rounded-lg border border-dashed border-white/15 bg-white/[0.02]">
                      <p className="text-xs text-white mb-1">Publishing Metadata defaults</p>
                      <p className="text-[11px] text-[#8A93A6]">Creator Profile → Publishing Metadata is demo-only today and doesn't save anything yet, so there are no shared or platform defaults to pull in automatically. Brand Keywords (Keywords tab) are the one part of this that's genuinely saved per creator right now.</p>
                      <Link to="/app/account/creator-profile" className="text-[11px] text-[#3A7BFF] hover:underline mt-1.5 inline-block" data-testid="optimize-metadata-creator-profile-link">Open Creator Profile →</Link>
                    </div>
                    {selectedBrandKit && (
                      <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02]">
                        <p className="text-xs text-white mb-1">From your Brand Kit</p>
                        <p className="text-[11px] text-[#8A93A6]">{selectedBrandKit.name}{selectedBrandKit.tagline ? ` — "${selectedBrandKit.tagline}"` : ''}</p>
                      </div>
                    )}
                  </div>
                )}
                {toolCategory === 'templates' && (
                  <EditorTemplatesPanel creatorId={project?.creator_id} templateKind="Optimization Template" filterTemplate={(et) => !!et.optimization} buildTemplatePayload={buildOptimizationTemplatePayload} onApply={applyOptimizationTemplate} applyHint="Fill in a title, description and keywords, then save it here as a reusable Optimization Template." projectId={projectId} episodeId={contentRef?.id} />
                )}
                {toolCategory === 'review' && (
                  <OptimizeReviewPanel contentRef={contentRef} variant={activeVariant} score={liveScore} resolvedThumbnail={resolvedThumbnail} brandKit={selectedBrandKit} onApprove={() => setReviewStatus('approved')} onReject={() => setReviewStatus('rejected')} onResetToDraft={() => setReviewStatus('draft')} disabled={!activeVariant} />
                )}
              </div>
            </div>
          )}

          {/* Center — the primary, always-visible workspace */}
          <div className="flex-1 min-w-0">
            <div className="glass rounded-xl p-4 lg:h-[560px] overflow-y-auto" data-testid="optimize-main-workspace">
              {!contentRef ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-3" data-testid="optimize-no-content-prompt">
                  <FolderOpen className="h-8 w-8 text-[#555E6E]" />
                  <p className="text-sm text-[#8A93A6] max-w-xs">Pick a video, clip, or image/post design from Media on the left to start optimizing it.</p>
                  <Button size="sm" className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" onClick={() => jumpTo('media')} data-testid="optimize-open-media-button">Open Media</Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">{contentRef.kind === 'video' ? 'Long-form Video' : contentRef.kind === 'clip' ? 'Clip' : 'Image / Post'}</p>
                      <h2 className="text-lg font-semibold truncate" data-testid="optimize-content-title">{contentRef.title}</h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="optimize-platform-switcher">
                    {PLATFORMS.map((p) => {
                      const exists = groupVariants.some((v) => v.platforms[0] === p.key);
                      const isActive = activePlatform === p.key;
                      return (
                        <button
                          key={p.key} onClick={() => (exists ? setActivePlatform(p.key) : togglePlatform(p.key))}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1 ${isActive ? 'border-[#3A7BFF] bg-[#3A7BFF]/15 text-white' : exists ? 'border-white/10 text-[#8A93A6] hover:text-white' : 'border-dashed border-white/15 text-[#555E6E] hover:text-white hover:border-white/30'}`}
                          data-testid={`optimize-platform-pill-${p.key}`}
                        >
                          {!exists && <Plus className="h-3 w-3" />}{p.label}
                        </button>
                      );
                    })}
                  </div>

                  {!activeVariant ? (
                    <div className="flex flex-col items-center justify-center text-center gap-3 py-10" data-testid="optimize-no-platform-prompt">
                      <Globe2 className="h-7 w-7 text-[#555E6E]" />
                      <p className="text-sm text-[#8A93A6] max-w-xs">Choose at least one platform above — each one gets its own independent title, description, keywords, thumbnail and score.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2 space-y-3">
                        <div>
                          <Label className="text-[11px] text-[#8A93A6]">Title</Label>
                          <Input value={activeVariant.title} onChange={(e) => patchActiveVariant({ title: e.target.value })} placeholder="Enter a title" className="glass mt-1" data-testid="optimize-field-title" />
                        </div>
                        <div>
                          <Label className="text-[11px] text-[#8A93A6]">Description / Caption</Label>
                          <Textarea value={activeVariant.description} onChange={(e) => patchActiveVariant({ description: e.target.value })} placeholder="Enter a description" className="glass mt-1" rows={3} data-testid="optimize-field-description" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-[11px] text-[#8A93A6]">CTA</Label>
                            <Input value={activeVariant.cta} onChange={(e) => patchActiveVariant({ cta: e.target.value })} placeholder="e.g. Follow for more" className="glass mt-1" data-testid="optimize-field-cta" />
                          </div>
                          <div>
                            <Label className="text-[11px] text-[#8A93A6]">Hashtags</Label>
                            <Input value={activeVariant.hashtags} onChange={(e) => patchActiveVariant({ hashtags: e.target.value })} placeholder="#example #tags" className="glass mt-1" data-testid="optimize-field-hashtags" />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-[#8A93A6]">Keywords ({activeVariant.keywords.brand.length + activeVariant.keywords.platform.length + activeVariant.keywords.content.length})</Label>
                            <button onClick={() => jumpTo('keywords')} className="text-[10px] text-[#3A7BFF] hover:underline" data-testid="optimize-edit-keywords-link">Edit all three buckets →</button>
                          </div>
                          <div className="flex flex-wrap gap-1.5" data-testid="optimize-keywords-summary">
                            {[...activeVariant.keywords.brand, ...activeVariant.keywords.platform, ...activeVariant.keywords.content].length === 0 ? (
                              <p className="text-xs text-[#555E6E]">No keywords yet.</p>
                            ) : (
                              [...activeVariant.keywords.brand, ...activeVariant.keywords.platform, ...activeVariant.keywords.content].map((k, i) => (
                                <Badge key={`${k}-${i}`} variant="secondary" className="bg-white/10 text-white border-white/10 text-[10px]">{k}</Badge>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-[#8A93A6]">Thumbnail</Label>
                            <button onClick={() => jumpTo('thumbnails')} className="text-[10px] text-[#3A7BFF] hover:underline" data-testid="optimize-change-thumbnail-link">Change →</button>
                          </div>
                          <div className="aspect-video rounded-lg border border-white/10 bg-black/40 flex items-center justify-center overflow-hidden" data-testid="optimize-thumbnail-main-preview">
                            {resolvedThumbnail.url ? <img src={resolvedThumbnail.url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-[#555E6E]" />}
                          </div>
                        </div>

                        {liveScore && (
                          <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="optimize-score-card">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[11px] text-[#8A93A6]">Optimization Score</span>
                              <Badge style={{ borderColor: scoreLabel(liveScore.overall).color, color: scoreLabel(liveScore.overall).color }} variant="outline" data-testid="optimize-score-badge">{liveScore.overall}/100</Badge>
                            </div>
                            {liveScore.suggestions[0] && <p className="text-[10px] text-[#8A93A6]">{liveScore.suggestions[0]}</p>}
                            <button onClick={() => jumpTo('review')} className="text-[10px] text-[#3A7BFF] hover:underline mt-1" data-testid="optimize-see-full-score-link">See full breakdown →</button>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={activeVariant.reviewStatus === 'approved'} onClick={() => setReviewStatus('approved')} data-testid="optimize-main-approve-button">Approve</Button>
                          <Button size="sm" variant="outline" className="flex-1 border-red-500/30 text-red-400" disabled={activeVariant.reviewStatus === 'rejected'} onClick={() => setReviewStatus('rejected')} data-testid="optimize-main-reject-button">Reject</Button>
                        </div>
                        <p className="text-[10px] text-[#555E6E] text-center" data-testid="optimize-main-status">Status: <span className="text-[#C0C7D4] capitalize">{activeVariant.reviewStatus}</span></p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
